import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DmInputTextLibComponent } from '@everymatrix-base-components/dm-input-text-lib';
import { DmButtonLibComponent } from '@everymatrix-base-components/dm-button-lib';
import {
  GlobalMessageService,
  ReportTemplate,
  SessionService,
  TemplateService,
  SendingService,
  SchedulesData,
  REPORTS,
  Schedule,
} from '@everymatrix-base-components/utils';
import { ProgressSpinner } from 'primeng/progressspinner';
import { forkJoin, from, of, switchMap, take } from 'rxjs';
import { RadioButtonModule } from 'primeng/radiobutton';

@Component({
  selector: 'lib-save-template-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    InputTextModule,
    ButtonModule,
    DmInputTextLibComponent,
    DmButtonLibComponent,
    ReactiveFormsModule,
    ProgressSpinner,
    RadioButtonModule,
  ],
  template: `
    <p-dialog
      header="Save Template"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '460px', 'height': showWarningMessage() ? '398px' : '340px' }"
      (onShow)="onShowDialog()"
    >
      @if(isTemplatePresent) {
      <div class="radio-buttons-container">
        <div class="radio-button">
          <p-radiobutton
            value="existing"
            inputId="existing"
            [formControl]="radioButtonControl"
          />
          <label for="existing" class="radio-button-label"
            >Save Changes To Existing Template</label
          >
        </div>
        <div class="radio-button">
          <p-radiobutton
            value="new"
            inputId="new"
            [formControl]="radioButtonControl"
          />
          <label for="new" class="radio-button-label"
            >Save As New Template</label
          >
        </div>
      </div>
      }
      <div class="fields-wrapper">
        <div class="field" [ngStyle]="{ 'margin-bottom': '12px' }">
          <lib-dm-input-text-lib
            label="Name"
            placeholder="name"
            [errorMessage]="hintMessage"
            [hint]="hintMessage"
            [control]="nameControl"
          />
        </div>
        <div class="field">
          <lib-dm-input-text-lib
            label="Description"
            placeholder="Description"
            [control]="descriptionControl"
          />
        </div>
        @if(showWarningMessage()) {
          <div class="warning-message">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              [ngStyle]="{ 'margin-right': '4px' }"
              >
                <path
                  d="M11.9998 7.99999V12M11.9998 16H12.0098M10.6151 2.89171L2.39019 17.0983C1.93398 17.8863 1.70588 18.2803 1.73959 18.6037C1.769 18.8857 1.91677 19.142 2.14613 19.3088C2.40908 19.5 2.86435 19.5 3.77487 19.5H20.2246C21.1352 19.5 21.5904 19.5 21.8534 19.3088C22.0827 19.142 22.2305 18.8857 22.2599 18.6037C22.2936 18.2803 22.0655 17.8863 21.6093 17.0983L13.3844 2.89171C12.9299 2.10654 12.7026 1.71396 12.4061 1.58211C12.1474 1.4671 11.8521 1.4671 11.5935 1.58211C11.2969 1.71396 11.0696 2.10655 10.6151 2.89171Z"
                  stroke="#506285"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
            </svg>
            @if(selectedDifferentSiteId()) {
              <span>Please note, saved template will be migrated to current selected domain and will not be available under child domain.</span>
            }

            @if(hasConfiguredSendOptions()) {
              <span>Template send options configurations will be removed!</span>
            }
          </div>
        }
      </div>
      <ng-template pTemplate="footer">
        <div
          style="width: 100%; display: flex; flex-direction: row; justify-content: space-between"
        >
          <lib-dm-button-lib
            text="Cancel"
            size="medium"
            variant="secondary-outlined"
            (outsideClickEmitter)="close()"
          />
          <lib-dm-button-lib
            text="Save"
            size="medium"
            variant="primary-filled"
            [disabled]="nameControl.invalid || descriptionControl.invalid"
            (outsideClickEmitter)="save()"
          >
            @if(loading()) {
            <div class="dm-button-icon" icon-left>
              <p-progress-spinner
                strokeWidth="6"
                [style]="{ width: '20px', height: '20px' }"
              />
            </div>
            }
          </lib-dm-button-lib>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styleUrls: ['./save-template-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaveTemplateModalComponent {
  visible = model(false);
  params = input.required();
  reportId = input<number | undefined>();
  loading = signal(false);
  saveEmitter = output();
  isTemplatePresent = false;
  isExisting = false;
  template: ReportTemplate | null | undefined;
  hintMessage =
    'Available symbols for template name: letters, numbers, underscores, dashes';

  templateService = inject(TemplateService);
  globalMessageService = inject(GlobalMessageService);
  sessionService = inject(SessionService);
  sendingService = inject(SendingService);

  nameControl = new FormControl('', [
    Validators.required,
    Validators.pattern(/^[A-Za-z0-9\-_]+$/),
  ]);
  descriptionControl = new FormControl('', [Validators.maxLength(255)]);
  radioButtonControl = new FormControl('existing');

  constructor() {
    effect(() => {
      this.template = this.templateService.template();
      const baseTemplate = this.templateService.baseTemplate();
      if (this.template && (!this.template.is_temporary || baseTemplate)) {
        this.isTemplatePresent = true;
        this.descriptionControl.setValue(baseTemplate?.template_desc || this.template.template_desc);
        this.nameControl.setValue(baseTemplate?.template_name || this.template.template_name);
      } else {
        this.isTemplatePresent = false;
        this.isExisting = false;
        this.descriptionControl.reset();
        this.nameControl.reset();
      }
    });
    this.radioButtonControl.valueChanges.subscribe((value) => {
      if (value === 'existing' && this.template) {
        this.isExisting = true;
        const baseTemplate = this.templateService.baseTemplate();
        this.descriptionControl.setValue(baseTemplate?.template_desc || this.template.template_desc);
        this.nameControl.setValue(baseTemplate?.template_name || this.template.template_name);
      } else {
        this.isExisting = false;
        this.descriptionControl.reset('');
        this.nameControl.reset('');
      }
    });
  }

  onShowDialog() {
    if (this.isTemplatePresent) {
      this.radioButtonControl.setValue('existing');
    } else {
      this.radioButtonControl.setValue('new');
    }
  }

  protected close(): void {
    this.visible.set(false);
    if (!this.isExisting) {
      this.nameControl.reset();
      this.descriptionControl.reset();
    }
  }

  protected save(): void {
    this.loading.set(true);
    const baseTemplate = this.templateService.baseTemplate();
    this.deleteSchedulesIfNeeded(baseTemplate || this.template).pipe(
        switchMap(() => 
          this.templateService
            .saveTemplate(
              this.nameControl.value as string,
              this.descriptionControl.value,
              this.params(),
              this.reportId(),
              this.radioButtonControl.value === 'existing'
                ? baseTemplate?.template_id || this.template?.template_id
                : null,
              false
            )
            .pipe(take(1))
        )
      )
      .subscribe({
        next: (template: any) => {
          this.radioButtonControl.setValue('existing');
          this.templateService.setSearchParams({
            modificationId: null,
            templateName: null,
            templateId: template.template_id,
          });
          this.templateService.templateChangedSubject.next(template);
          this.templateService.updateTemplateList(template);
          this.templateService.template.set(template);
          this.globalMessageService.showSuccessMessage({
            detail: 'The Report has been saved successfully',
          });
          this.loading.set(false);
          this.close();
        },
        error: (err) => {
          this.globalMessageService.showErrorMessage({
            detail: err,
          });
          this.loading.set(false);
        },
      });
  }

  protected hasConfiguredSendOptions  () {
    const baseTemplate = this.templateService.baseTemplate();
    return baseTemplate?.input_params.send_option || this.template?.input_params.send_option;
  }

  protected selectedDifferentSiteId = () => {
    return this.sessionService.sessionData?.Domains && this.template?.domain_id && +this.template?.domain_id != +this.sessionService.sessionData.Domains;
  }

  protected showWarningMessage = () => {
    return this.isTemplatePresent && this.isExisting && (this.selectedDifferentSiteId() || this.hasConfiguredSendOptions());
  }

  protected deleteSchedulesIfNeeded(
    template: ReportTemplate | null | undefined
  ) {
    const reportKey = Object.values(REPORTS).find((r) => r.reportId === template?.report_id)?.reportKey;
    if (!template || !template?.input_params.send_option || this.radioButtonControl.value !== 'existing' || !reportKey) {
      return of(null)
    }
    return this.getSchedules(template, reportKey).pipe(
      take(1),
      switchMap((schedulesData: SchedulesData) => {
        const deleteObservables = schedulesData.flatMap((schedule) => schedule.schedules.map((scheduleItem: Schedule) => from(this.sendingService.deleteSchedule(template.domain_id, scheduleItem.schedule_id, scheduleItem.schedule_group_id))))
        return deleteObservables.length > 0
        ? forkJoin(deleteObservables)
        : of(null);
      })
    )
  }

  protected getSchedules(template: ReportTemplate, reportKey: string) {
    return this.sendingService
      .getSchedules(
        reportKey,
        template.template_id,
        template.domain_id
      )
  }
}
